import React, { createContext, useState, useContext, useEffect } from 'react';

// API Gateway endpoint URL (replace with your actual URL)
const API_ENDPOINT = "https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/checkGuest";

const AuthContext = createContext();

const SESSION_EXPIRY = 7 * 24 * 60 * 60 * 1000; // 7 days


export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

// Helper function to check if session is expired
  const isSessionExpired = (timestamp) => {
    if (!timestamp) return true;
    
    const sessionTime = new Date(timestamp).getTime();
    const currentTime = new Date().getTime();
    
    return currentTime - sessionTime > SESSION_EXPIRY;
  };
  
  useEffect(() => {
    // Check if user is already logged in (session storage)
    const checkExistingSession = async () => {
      const session = sessionStorage.getItem('weddingGuestSession');
      
      if (session) {
        try {
          const sessionData = JSON.parse(session);
          
          // Verify email via API Gateway
          const response = await fetch(API_ENDPOINT, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email: sessionData.email })
          });
          
          const data = await response.json();
          
          if (data.success) {
            setCurrentUser(data.guest);
          } else {
            // Email no longer valid, clear session
            sessionStorage.removeItem('weddingGuestSession');
          }
        } catch (err) {
          console.error("Session restoration error:", err);
          sessionStorage.removeItem('weddingGuestSession');
        }
      }
      
      setLoading(false);
    };
    
    checkExistingSession();
  }, []);
  
  // Verify email via API Gateway
  const login = async (email) => {
    setLoading(true);
    setError("");
    
    try {
      // Normalize email to lowercase
      const normalizedEmail = email.toLowerCase().trim();
      
      // Call API Gateway endpoint
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: normalizedEmail })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Guest exists, set user and create session
        setCurrentUser(data.guest);
        
        // Store in session storage
        sessionStorage.setItem('weddingGuestSession', JSON.stringify({
          email: normalizedEmail,
          name: data.guest.name || 'Guest',
          timestamp: new Date().toISOString()
        }));
        
        return true;
      } else {
        // Guest not found
        setError(data.message || "Sorry, this email is not on the guest list.");
        return false;
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("An error occurred connecting to the server. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  const logout = () => {
    setCurrentUser(null);
    sessionStorage.removeItem('weddingGuestSession');
  };
  
  const value = {
    currentUser,
    login,
    logout,
    loading,
    error
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};